import { useEffect, useRef } from "react";
import { Box, Divider, Grid2 as Grid, Paper, Stack, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { AgreementsAutocomplete } from "@shared/components/AutocompleteFields";
import { IntegrationCatalog } from "@modules/erpIntegrations/types";
import { ConfigFormValues } from "@modules/erpIntegrations/validations";

import { CopyTextButton } from "../CopyTextButton";
import { CredentialFields } from "../CredentialFields";

const LEGACY_CREDENTIAL_ALIASES: Record<string, string> = {
  username: "name",
  client_secret: "token",
};

interface ConfigFormProps {
  integrations: IntegrationCatalog[];
  resetCredentialsOnIntegrationChange?: boolean;
  lockPair?: boolean;
  mode?: "create" | "edit";
}

export function ConfigForm({
  integrations,
  resetCredentialsOnIntegrationChange = false,
  lockPair = false,
  mode = "create",
}: ConfigFormProps) {
  const translate = useTranslate();
  const { setValue, getValues } = useFormContext<ConfigFormValues>();
  const integrationId = useWatch({ name: "integration" });
  const credentials = useWatch({ name: "credentials" }) ?? {};
  const testDocument = useWatch({ name: "test_document" }) ?? "";
  const previousIntegration = useRef(integrationId);

  const selected = integrations.find(
    (item) => item.id === Number(integrationId)
  );
  const integrationDetail = (
    getValues() as ConfigFormValues & {
      integration_detail?: IntegrationCatalog;
    }
  ).integration_detail;
  const detailSchema = integrationDetail?.credential_schema;
  const instructions = (selected?.instructions || integrationDetail?.instructions || "").trim();
  const schema = selected?.credential_schema ?? detailSchema ?? [];
  const planMapOptions = selected?.plan_map_options ?? [];

  useEffect(() => {
    if (mode !== "edit") return;
    const schemaKeys = new Set(schema.map((field) => field.key));
    for (const [schemaKey, legacyKey] of Object.entries(LEGACY_CREDENTIAL_ALIASES)) {
      if (!schemaKeys.has(schemaKey) || schemaKeys.has(legacyKey)) continue;
      const current = credentials[schemaKey];
      const legacy = credentials[legacyKey];
      if (
        (current === undefined || current === null || current === "") &&
        legacy !== undefined &&
        legacy !== null &&
        legacy !== ""
      ) {
        setValue(`credentials.${schemaKey}`, legacy);
      }
    }
  }, [mode, schema, credentials, setValue]);

  useEffect(() => {
    if (!resetCredentialsOnIntegrationChange) return;
    if (
      previousIntegration.current !== undefined &&
      previousIntegration.current !== integrationId
    ) {
      setValue("credentials", {});
      const defaultOption = integrations
        .find((item) => item.id === Number(integrationId))
        ?.plan_map_options?.find((option) => option.is_default);
      setValue("plan_map_option", defaultOption?.id ?? null);
    }
    previousIntegration.current = integrationId;
  }, [integrationId, integrations, resetCredentialsOnIntegrationChange, setValue]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <AgreementsAutocomplete
          name="agreement"
          label={translate("v1/agreement_integration_config_admin.fields.agreement")}
          required
          disabled={lockPair}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Form.Select
          name="integration"
          label={translate(
            "v1/agreement_integration_config_admin.fields.integration"
          )}
          options={integrations.map((item) => ({
            value: item.id,
            label: item.name,
          }))}
          required
          disabled={lockPair}
        />
      </Grid>
      {instructions && (
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2">
                {translate("v1/integration_admin.fields.instructions")}
              </Typography>
              <CopyTextButton text={instructions} />
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
              {instructions}
            </Typography>
          </Paper>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="active"
          label={translate("v1/agreement_integration_config_admin.fields.active")}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Form.Input
              name="test_document"
              label={translate("v1/agreement_integration_config_admin.fields.test_document")}
              mask="document"
            />
          </Box>
          <CopyTextButton text={testDocument} />
        </Stack>
      </Grid>
      {(schema.length > 0 || mode === "edit") && (
        <Grid size={{ xs: 12 }}>
          {mode === "edit" ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {schema.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                    {translate(
                      "v1/agreement_integration_config_admin.fields.credentials"
                    )}
                  </Typography>
                  <CredentialFields schema={schema} />
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  border: "1px dashed",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {translate("v1/agreement_integration_config_admin.fields.access_token_hint")}
                </Typography>
                <Form.Input
                  name="access_token"
                  label={translate("v1/agreement_integration_config_admin.fields.access_token")}
                />
              </Box>
            </Paper>
          ) : (
            <CredentialFields schema={schema} />
          )}
        </Grid>
      )}
      {planMapOptions.length > 0 && (
        <Grid size={{ xs: 12 }}>
          {mode === "edit" ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Form.Select
                name="plan_map_option"
                label={translate(
                  "v1/agreement_integration_config_admin.fields.plan_map_option_column"
                )}
                options={planMapOptions.map((option) => ({
                  value: option.id,
                  label: option.label,
                }))}
              />
            </Paper>
          ) : (
            <Form.Select
              name="plan_map_option"
              label={translate(
                "v1/agreement_integration_config_admin.fields.plan_map_option"
              )}
              options={planMapOptions.map((option) => ({
                value: option.id,
                label: option.label,
              }))}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
}
